'use client';

import { useEffect, useRef } from 'react';

// ── Types ───────────────────────────────────────────────────────

export interface NotificationPayload {
  id: string;
  type: string;
  title: string;
  body: string | null;
  metadata: Record<string, unknown>;
  isRead: boolean;
  createdAt: string;
}

// ── WebSocket URL ────────────────────────────────────────────────
const WS_BASE = import.meta.env.MODE === 'production'
  ? `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`
  : (import.meta.env.VITE_WS_URL || 'ws://localhost:3001');

// ── Module-level singleton (survives StrictMode double-mounts) ──
//
// The WS instance lives OUTSIDE React's component lifecycle.
// This means StrictMode's mount → unmount → remount cycle cannot
// cause a close() on a CONNECTING socket — because the cleanup
// function simply does NOT touch the singleton.
//
// The browser warning "WebSocket is closed before the connection is
// established" fires at the C++ level (not through JS onerror), so
// the ONLY way to suppress it is to never call close() on a socket
// whose readyState === CONNECTING.
let _ws: WebSocket | null = null;
let _token: string | null = null;          // token used for current connection
let _intentionalClose = false;
let _reconnectAttempt = 0;
let _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let _onMessage: ((p: NotificationPayload) => void) | null = null;

function _buildUrl(token: string): string {
  const base = WS_BASE.endsWith('/ws') ? WS_BASE : `${WS_BASE}/ws`;
  return `${base}/notifications?token=${encodeURIComponent(token)}`;
}

/** Create a new WebSocket and attach handlers */
function _openConnection(token: string): void {
  if (_ws) return; // should never happen, but guard anyway

  _token = token;
  _intentionalClose = false;

  const ws = new WebSocket(_buildUrl(token));
  _ws = ws;

  ws.onopen = () => {
    _reconnectAttempt = 0;
    _intentionalClose = false;
  };

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data as string);
      if (data.type === 'notification.new' && data.payload && _onMessage) {
        _onMessage(data.payload as NotificationPayload);
      }
    } catch {
      // Ignore malformed messages
    }
  };

  ws.onclose = (_event) => {
    _ws = null;
    if (_intentionalClose || _event.code === 1000) {
      _intentionalClose = false;
      return;
    }
    // Auto-reconnect with exponential backoff (max 5 attempts)
    if (_reconnectAttempt < 5 && _token) {
      const delay = Math.min(
        2000 * Math.pow(2, _reconnectAttempt) + Math.random() * 500,
        30000,
      );
      _reconnectAttempt += 1;
      _reconnectTimer = setTimeout(() => {
        _openConnection(_token!);
      }, delay);
    }
  };

  ws.onerror = () => {
    // Triggers onclose
  };
}

/**
 * Ensure a live connection for the given token.
 * No-op if already connected with the same token.
 */
function _ensureConnected(
  token: string,
  onMessage: (p: NotificationPayload) => void,
): void {
  _onMessage = onMessage;

  // Already open with the same token → nothing to do
  if (_ws && _ws.readyState === WebSocket.OPEN && _token === token) {
    return;
  }

  // Currently connecting with the same token → let it finish
  if (_ws && _ws.readyState === WebSocket.CONNECTING && _token === token) {
    return;
  }

  // Need to connect or reconnect
  _disconnect(false); // Don't clear token — we're about to reconnect
  _openConnection(token);
}

/**
 * Disconnect the current connection.
 * @param clearToken - If true, also forget the token (used on logout/disable)
 */
function _disconnect(clearToken = true): void {
  if (_reconnectTimer) {
    clearTimeout(_reconnectTimer);
    _reconnectTimer = null;
  }

  if (!_ws) {
    if (clearToken) _token = null;
    return;
  }

  _intentionalClose = true;

  // Only call close() on OPEN sockets.
  // Calling close() on CONNECTING sockets triggers the browser-level
  // warning "WebSocket is closed before the connection is established"
  // which CANNOT be suppressed from JavaScript code.
  if (_ws.readyState === WebSocket.OPEN) {
    _ws.close(1000, 'Disconnect');
  }
  // For CONNECTING / CLOSING states: abandon silently.
  // The orphaned socket will eventually time out or error on its own.
  // We set onerror/onclose to no-ops so it doesn't trigger reconnect logic.

  _ws.onclose = () => {}; // Suppress reconnect from abandoned socket
  _ws.onerror = () => {};
  // Intentionally NOT calling _ws.close() for non-OPEN states

  _ws = null;
  if (clearToken) _token = null;
}

// ── Public hook ──────────────────────────────────────────────────

interface UseNotificationWebSocketOptions {
  token: string | null;
  enabled: boolean;
  onNotification: (notification: NotificationPayload) => void;
}

export function useNotificationWebSocket({
  token,
  enabled,
  onNotification,
}: UseNotificationWebSocketOptions) {
  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  // Main effect: tell the singleton what to do
  useEffect(() => {
    if (!enabled || !token) {
      _disconnect(true); // Logout / disabled → fully disconnect
      return;
    }

    // Connect (or confirm existing connection)
    _ensureConnected(token, onNotificationRef.current);

    // Cleanup: DO NOTHING to the singleton!
    // The singleton survives StrictMode double-mounts.
    // We only disconnect when explicitly disabled (above) or unmounted
    // while disabled (handled by the next mount's effect run).
    //
    // Note: we DO disconnect on cleanup if we're being disabled,
    // but that case is handled by the `if (!enabled)` branch above
    // running before the cleanup from the previous render.
    return () => {
      // Intentionally empty — singleton persists across mounts
      // This is the key insight that eliminates the warning.
    };
  }, [enabled, token]);

  // Reconnect on visibility change (tab becomes visible again)
  useEffect(() => {
    if (!enabled || !token) return;

    function handleVisibility() {
      if (
        document.visibilityState === 'visible' &&
        (!_ws || _ws.readyState !== WebSocket.OPEN)
      ) {
        _reconnectAttempt = 0;
        if (token) {
          _ensureConnected(token, onNotificationRef.current);
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [enabled, token]);
}
